/**
 * Tailor CV / Cover Letter Modal
 * CV tab rewords the user's EXISTING CV content (reordered skills, reworded descriptions) to
 * surface keywords relevant to a specific job. Cover letter tab drafts a letter from the same
 * profile + job data, opt-in, editable before download. Neither tab invents new experience;
 * truthfulness is enforced server-side by sanitizers, not by the prompts.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { X, Sparkle, Download, Copy, Check, WarningCircle } from '@phosphor-icons/react';
import { trackEvent } from '../../../../../utils/analytics/analytics';
import { useAuth } from '../../../../../hooks/useAuth';
import { useAppSelector } from '../../../../../redux/hooks';
import { useEffectiveUserProfilePhoto } from '../../../../../hooks/useEffectiveUserProfilePhoto';
import { useApplicationKitActions } from '../../../../../hooks/useApplicationKitActions';
import { EuropassCVContent } from '../../../../../components/EuropassCVContent';
import { deriveSortedCvEntries } from '../../../../../utils/profile/derive-cv-entries';
import { buildTailoredCvData } from '../../../../../utils/profile/build-tailored-cv-data';
import { pickProfileLocaleContent } from '../../../../../utils/profile/pick-profile-locale-content';
import { normalizeSupportedLocale } from '../../../../../utils/routing/locale-from-pathname';
import { stableHash } from '../../../../../utils/text/stable-hash';
import type { CvData } from '../../../../../utils/profile/generate-cv-pdf';
import type { TailorCvJobInput, TailorCvProfileInput, TailorCvResponse } from '../../../../../types/profile-cv-tailor';
import type { CoverLetterResponse } from '../../../../../types/profile-cover-letter';
import type { ApplicationKitState } from '../../../../../types/user-state';
import type { DisplayJob } from '../../../../../types';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';

const LETTER_EDIT_PERSIST_DEBOUNCE_MS = 1500;

export interface TailorCvModalProps {
  job: DisplayJob | null;
  applyUrl: string;
  isOpen: boolean;
  onClose: () => void;
  /** Which tab to open onto; e.g. a `?tailor=letter` deep link. Defaults to 'cv'. */
  initialTab?: 'cv' | 'letter';
}

function formatDateRange(start: string, end: string): string {
  if (!end) return start;
  return `${start} – ${end}`;
}

/** Joins the letter's parts into the single editable block the textarea shows; user edits from here win over the original model output everywhere downstream (copy, PDF). */
function buildLetterText(letter: CoverLetterResponse, userName?: string | null): string {
  const body = letter.paragraphs.map((p) => p.text).join('\n\n');
  return [letter.greeting, body, letter.closing, userName ?? '']
    .map((part) => part.trim())
    .filter(Boolean)
    .join('\n\n');
}

type TailorState = 'empty-profile' | 'loading' | 'error' | 'quota-exceeded' | 'success';
type LetterState = 'idle' | 'loading' | 'error' | 'quota-exceeded' | 'success';
type ModalTab = 'cv' | 'letter';

export const TailorCvModal: React.FC<TailorCvModalProps> = ({ job, applyUrl, isOpen, onClose, initialTab }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { user } = useAuth();
  const userPhoto = useEffectiveUserProfilePhoto(user);
  // Tailor using whichever language CV matches this job page (falling back to whichever
  // language the user has actually filled in, if that one's empty); so applying to a job
  // in Finnish uses the Finnish CV, not whatever tab happened to be open on the profile page.
  const pageLocale = normalizeSupportedLocale(intl.locale);
  const profileCvByLocale = useAppSelector((s) => s.jobForm.profileCvByLocale);
  const { profileTitle, profileWorkExperiences: workExperiences, profileEducation: education, profileProjects: projects, profileSkills: skills } =
    pickProfileLocaleContent(profileCvByLocale, pageLocale);

  const [activeTab, setActiveTab] = useState<ModalTab>('cv');

  const [state, setState] = useState<TailorState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tailoredData, setTailoredData] = useState<CvData | null>(null);
  const [keywordsHighlighted, setKeywordsHighlighted] = useState<string[]>([]);
  const requestedRef = useRef(false);

  const [letterState, setLetterState] = useState<LetterState>('idle');
  const [letterErrorMessage, setLetterErrorMessage] = useState<string | null>(null);
  const [letterResponse, setLetterResponse] = useState<CoverLetterResponse | null>(null);
  const [letterText, setLetterText] = useState('');
  const [generatedLetterText, setGeneratedLetterText] = useState('');
  const [copied, setCopied] = useState(false);
  const letterEditFiredRef = useRef(false);
  const letterPersistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { getApplicationKit, saveApplicationKit } = useApplicationKitActions();

  const hasAnyContent = workExperiences.length > 0 || skills.length > 0 || projects.length > 0;

  const buildJobPayload = (): TailorCvJobInput => ({
    title: job?.title,
    normalisedTitle: job?.normalisedTitle,
    companyName: job?.companyName,
    description: job?.description,
    requirements: job?.requirements,
    responsibilities: job?.responsibilities,
    skillsAndTechnologies: job?.skillsAndTechnologies,
    specialisations: job?.specialisations,
    educationLevel: job?.educationLevel,
    minimumYearsOfExperience: job?.minimumYearsOfExperience,
    peopleManagementTier: job?.peopleManagementTier,
    requiredLanguages: job?.requiredLanguages,
    optionalLanguages: job?.optionalLanguages,
    employerOverview: job?.employerOverview,
    companyCoreValues: job?.companyCoreValues,
  });

  const buildProfilePayload = (): TailorCvProfileInput => ({
    profileTitle,
    workExperiences: workExperiences.map((e) => ({ id: e.id, title: e.title, company: e.company, description: e.description })),
    projects: projects.map((p) => ({ id: p.id, title: p.title, description: p.description })),
    skills,
    // Context only; never rewritten, see the sanitizers and system prompts.
    education: education.map((e) => ({ id: e.id, degree: e.degree, institution: e.institution })),
  });

  const resolveSourceLabel = (id: string): string => {
    if (id === 'skills') {
      return formatMessage({
        id: 'jobDetailPage.coverLetter.sourceSkills',
        defaultMessage: 'Skills',
        description: 'Provenance chip label for a cover letter paragraph based on the skills list',
      });
    }
    if (id === 'company') {
      return formatMessage({
        id: 'jobDetailPage.coverLetter.sourceCompany',
        defaultMessage: 'Company info',
        description: 'Provenance chip label for a cover letter paragraph based on employer overview/core values',
      });
    }
    return (
      workExperiences.find((e) => e.id === id)?.title ??
      projects.find((p) => p.id === id)?.title ??
      education.find((e) => e.id === id)?.degree ??
      id
    );
  };

  // Identifies the exact profile snapshot a cached kit was generated from; cache-on-reopen
  // only reuses a stored kit when this matches, so an edited profile always regenerates fresh.
  const buildProfileHash = (): string => stableHash(buildProfilePayload());

  const persistKit = (partial: Pick<ApplicationKitState, 'tailored'> | Pick<ApplicationKitState, 'coverLetter'>) => {
    const jobId = job?.objectID;
    if (!jobId) return;
    const profileHash = buildProfileHash();
    const existingKit = getApplicationKit(jobId);
    const stillFresh = Boolean(existingKit && existingKit.profileHash === profileHash && existingKit.locale === pageLocale);
    const nextKit: ApplicationKitState = {
      jobTitle: job?.title ?? job?.normalisedTitle ?? '',
      companyName: job?.companyName ?? '',
      companyId: job?.companyId ?? '',
      locale: pageLocale,
      profileHash,
      tailored: stillFresh ? existingKit?.tailored : undefined,
      coverLetter: stillFresh ? existingKit?.coverLetter : undefined,
      ...partial,
      updatedAt: new Date().toISOString(),
    };
    void saveApplicationKit(jobId, nextKit);
  };

  // Cleanup the debounced letter-edit persist timer on unmount.
  useEffect(() => {
    return () => {
      if (letterPersistTimeoutRef.current) clearTimeout(letterPersistTimeoutRef.current);
    };
  }, []);

  // Lock body scroll and close on Escape while open.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Opens onto the requested tab (e.g. a `?tailor=letter` deep link) each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    const tab = initialTab ?? 'cv';
    setActiveTab(tab);
    if (tab === 'letter') {
      trackEvent({
        name: 'cover_letter_interaction',
        params: { action: 'open_tab', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTab]);

  // Reset so reopening (e.g. for a different job) fetches/starts fresh.
  useEffect(() => {
    if (isOpen) return;
    requestedRef.current = false;
    setActiveTab('cv');
    setLetterState('idle');
    setLetterErrorMessage(null);
    setLetterResponse(null);
    setLetterText('');
    setGeneratedLetterText('');
    setCopied(false);
    letterEditFiredRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || requestedRef.current) return;

    if (!hasAnyContent) {
      setState('empty-profile');
      return;
    }

    requestedRef.current = true;
    setErrorMessage(null);

    const baseCvData: CvData = {
      userName: user?.name ?? null,
      userPhoto,
      profileTitle,
      ...deriveSortedCvEntries({ workExperiences, education, projects }),
      skills,
    };

    // Cache-on-reopen: a kit generated from this exact profile snapshot, for this locale,
    // is reused without another paid call; refreshing the tab shouldn't burn quota.
    const jobId = job?.objectID;
    const cachedKit = jobId ? getApplicationKit(jobId) : null;
    const profileHash = buildProfileHash();
    if (cachedKit?.tailored && cachedKit.profileHash === profileHash && cachedKit.locale === pageLocale) {
      setTailoredData(buildTailoredCvData(baseCvData, cachedKit.tailored));
      setKeywordsHighlighted(cachedKit.tailored.keywordsHighlighted);
      setState('success');
      trackEvent({
        name: 'tailor_cv_interaction',
        params: { action: 'cache_hit', job_id: jobId, company_id: job?.companyId, locale: intl.locale, source: 'job_page' },
      });
      return;
    }

    setState('loading');

    const run = async () => {
      try {
        const res = await fetch('/api/profile-cv-tailor', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ job: buildJobPayload(), profile: buildProfilePayload() }),
        });
        const data = (await res.json()) as { tailored?: TailorCvResponse; message?: string; error?: string };

        if (!res.ok || !data.tailored) {
          if (res.status === 429 && data.error === 'quota_exceeded') {
            trackEvent({
              name: 'tailor_cv_interaction',
              params: { action: 'quota_exceeded', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale, source: 'job_page' },
            });
            setState('quota-exceeded');
            return;
          }
          trackEvent({
            name: 'tailor_cv_interaction',
            params: { action: 'generate_error', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale, source: 'job_page' },
          });
          if (res.status === 503 && data.error === 'service_unavailable') {
            setErrorMessage(
              formatMessage({
                id: 'jobDetailPage.tailorCv.notConfigured',
                defaultMessage: 'CV tailoring is not enabled for this environment.',
                description: 'Shown when OPENAI_API_KEY is missing for CV tailoring',
              })
            );
          } else {
            setErrorMessage(
              data.message ||
                data.error ||
                formatMessage({
                  id: 'jobDetailPage.tailorCv.failed',
                  defaultMessage: 'Could not tailor your CV. Try again later.',
                  description: 'Generic CV tailoring failure',
                })
            );
          }
          setState('error');
          return;
        }

        setTailoredData(buildTailoredCvData(baseCvData, data.tailored));
        setKeywordsHighlighted(data.tailored.keywordsHighlighted);
        persistKit({ tailored: data.tailored });
        trackEvent({
          name: 'tailor_cv_interaction',
          params: { action: 'generate_success', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale, source: 'job_page' },
        });
        setState('success');
      } catch (e) {
        trackEvent({
          name: 'tailor_cv_interaction',
          params: { action: 'generate_error', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale, source: 'job_page' },
        });
        setErrorMessage(
          e instanceof Error
            ? e.message
            : formatMessage({
                id: 'jobDetailPage.tailorCv.networkError',
                defaultMessage: 'Network error. Check your connection.',
                description: 'CV tailoring network failure',
              })
        );
        setState('error');
      }
    };

    void run();
    // Deliberately re-runs only when the modal opens or the profile/job snapshot at that
    // moment changes identity; refetching on every keystroke elsewhere isn't desired.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleTabChange = (tab: ModalTab) => {
    setActiveTab(tab);
    if (tab === 'letter') {
      trackEvent({
        name: 'cover_letter_interaction',
        params: { action: 'open_tab', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale },
      });
    }
  };

  const handleGenerateLetter = async () => {
    letterEditFiredRef.current = false;
    setCopied(false);

    // Cache-on-reopen: a letter generated from this exact profile snapshot, for this locale,
    // is reused without another paid call. Cached hydration has no structured CoverLetterResponse
    // (only the final text is persisted), so provenance chips/warnings simply don't render for it.
    const jobId = job?.objectID;
    const cachedKit = jobId ? getApplicationKit(jobId) : null;
    const profileHash = buildProfileHash();
    if (cachedKit?.coverLetter && cachedKit.profileHash === profileHash && cachedKit.locale === pageLocale) {
      setLetterResponse(null);
      setLetterText(cachedKit.coverLetter.text);
      setGeneratedLetterText(cachedKit.coverLetter.text);
      setLetterState('success');
      return;
    }

    trackEvent({
      name: 'cover_letter_interaction',
      params: { action: 'generate', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale },
    });
    setLetterState('loading');
    setLetterErrorMessage(null);

    try {
      const res = await fetch('/api/profile-cover-letter', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job: buildJobPayload(), profile: buildProfilePayload(), locale: pageLocale }),
      });
      const data = (await res.json()) as { coverLetter?: CoverLetterResponse; message?: string; error?: string };

      if (!res.ok || !data.coverLetter) {
        if (res.status === 429 && data.error === 'quota_exceeded') {
          trackEvent({
            name: 'cover_letter_interaction',
            params: { action: 'quota_exceeded', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale },
          });
          setLetterState('quota-exceeded');
          return;
        }
        trackEvent({
          name: 'cover_letter_interaction',
          params: { action: 'generate_error', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale },
        });
        if (res.status === 503 && data.error === 'service_unavailable') {
          setLetterErrorMessage(
            formatMessage({
              id: 'jobDetailPage.coverLetter.notConfigured',
              defaultMessage: 'Cover letter drafting is not enabled for this environment.',
              description: 'Shown when OPENAI_API_KEY is missing for cover letter drafting',
            })
          );
        } else {
          setLetterErrorMessage(
            data.message ||
              data.error ||
              formatMessage({
                id: 'jobDetailPage.coverLetter.failed',
                defaultMessage: 'Could not draft your cover letter. Try again later.',
                description: 'Generic cover letter drafting failure',
              })
          );
        }
        setLetterState('error');
        return;
      }

      const letter = data.coverLetter;
      const text = buildLetterText(letter, user?.name);
      setLetterResponse(letter);
      setLetterText(text);
      setGeneratedLetterText(text);
      persistKit({ coverLetter: { text } });
      trackEvent({
        name: 'cover_letter_interaction',
        params: {
          action: 'generate_success',
          job_id: job?.objectID,
          company_id: job?.companyId,
          locale: intl.locale,
          ungrounded_count: letter.paragraphs.filter((p) => p.ungrounded).length,
          overclaim_count: letter.possibleOverclaims.length,
        },
      });
      setLetterState('success');
    } catch (e) {
      trackEvent({
        name: 'cover_letter_interaction',
        params: { action: 'generate_error', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale },
      });
      setLetterErrorMessage(
        e instanceof Error
          ? e.message
          : formatMessage({
              id: 'jobDetailPage.coverLetter.networkError',
              defaultMessage: 'Network error. Check your connection.',
              description: 'Cover letter drafting network failure',
            })
      );
      setLetterState('error');
    }
  };

  const handleLetterTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextText = e.target.value;
    setLetterText(nextText);
    if (!letterEditFiredRef.current) {
      letterEditFiredRef.current = true;
      trackEvent({
        name: 'cover_letter_interaction',
        params: { action: 'edit', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale },
      });
    }
    if (letterPersistTimeoutRef.current) clearTimeout(letterPersistTimeoutRef.current);
    letterPersistTimeoutRef.current = setTimeout(() => {
      persistKit({ coverLetter: { text: nextText } });
    }, LETTER_EDIT_PERSIST_DEBOUNCE_MS);
  };

  const handleDownload = async () => {
    if (!tailoredData) return;
    trackEvent({
      name: 'tailor_cv_interaction',
      params: { action: 'download', job_id: job?.objectID, company_id: job?.companyId, locale: intl.locale, source: 'job_page' },
    });
    const { generateCvPdf } = await import('../../../../../utils/profile/generate-cv-pdf');
    const doc = generateCvPdf(tailoredData, pageLocale);
    const companySuffix = job?.companyName ? `-${job.companyName}` : '';
    doc.save(`${user?.name || 'cv'}${companySuffix}-tailored-cv.pdf`);
  };

  const handleCopyLetter = async () => {
    try {
      await navigator.clipboard.writeText(letterText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied/unavailable; the text is still visible to select manually.
    }
    trackEvent({
      name: 'cover_letter_interaction',
      params: {
        action: 'copy',
        job_id: job?.objectID,
        company_id: job?.companyId,
        locale: intl.locale,
        edited: letterText !== generatedLetterText,
      },
    });
  };

  const handleDownloadLetter = async () => {
    trackEvent({
      name: 'cover_letter_interaction',
      params: {
        action: 'download',
        job_id: job?.objectID,
        company_id: job?.companyId,
        locale: intl.locale,
        edited: letterText !== generatedLetterText,
      },
    });
    const { generateCoverLetterPdf } = await import('../../../../../utils/profile/generate-cover-letter-pdf');
    // Renders the CURRENT textarea content; user edits win over the original model output.
    const doc = generateCoverLetterPdf(
      { userName: user?.name ?? null, profileTitle, companyName: job?.companyName ?? null, text: letterText },
      pageLocale
    );
    const companySuffix = job?.companyName ? `-${job.companyName}` : '';
    doc.save(`${user?.name || 'cover-letter'}${companySuffix}-cover-letter.pdf`);
  };

  if (!isOpen) return null;

  const groundedParagraphs = letterResponse?.paragraphs.filter((p) => !p.ungrounded) ?? [];
  const ungroundedCount = letterResponse?.paragraphs.filter((p) => p.ungrounded).length ?? 0;
  const overclaims = letterResponse?.possibleOverclaims ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={formatMessage({
        id: 'jobDetailPage.tailorCv.title',
        defaultMessage: 'Tailor my CV for this job',
        description: 'Tailor CV modal title',
      })}
    >
      <div
        className="relative flex flex-col max-h-[90vh] w-full max-w-2xl bg-white dark:bg-[#1F1509] rounded-2xl shadow-2xl overflow-hidden border border-[#DDD5C8]/60 dark:border-[#4A3520]/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDD5C8] dark:border-[#4A3520] shrink-0 bg-[#F2EADF]/40 dark:bg-[#2A1D0F]/50">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-r from-[#3B5E45] to-[#5A8A68] text-white">
              <Sparkle className="h-4 w-4" />
            </span>
            <span className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
              <Msg id="jobDetailPage.tailorCv.title" defaultMessage="Tailor my CV for this job" description="Tailor CV modal title" />
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#EDE3D4] dark:hover:bg-[#342415] text-[#7A6E5F] dark:text-[#B8A48C] cursor-pointer"
            aria-label={formatMessage({ id: 'profile.closeSuggestions', defaultMessage: 'Close', description: 'Close AI suggestions' })}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {hasAnyContent && (
          <div className="px-5 pt-4 shrink-0">
            <div role="tablist" className="inline-flex rounded-lg border border-[#DDD5C8] dark:border-[#4A3520] p-0.5 bg-[#F2EADF]/60 dark:bg-[#2A1D0F]/40">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'cv'}
                onClick={() => handleTabChange('cv')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  activeTab === 'cv'
                    ? 'bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] shadow-sm'
                    : 'text-[#7A6E5F] dark:text-[#B8A48C] hover:text-[#1E1A14] dark:hover:text-[#F2EADF]'
                }`}
              >
                <Msg id="jobDetailPage.tailorCv.tabCv" defaultMessage="CV" description="Segmented control label for the CV tab" />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'letter'}
                onClick={() => handleTabChange('letter')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  activeTab === 'letter'
                    ? 'bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] shadow-sm'
                    : 'text-[#7A6E5F] dark:text-[#B8A48C] hover:text-[#1E1A14] dark:hover:text-[#F2EADF]'
                }`}
              >
                <Msg
                  id="jobDetailPage.tailorCv.tabCoverLetter"
                  defaultMessage="Cover letter"
                  description="Segmented control label for the cover letter tab"
                />
              </button>
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {!hasAnyContent && (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-[#3D3529] dark:text-[#B8A48C]">
                <Msg
                  id="jobDetailPage.tailorCv.emptyProfile"
                  defaultMessage="Fill in your profile first: we need your work experience or skills to tailor a CV."
                  description="Shown when profile has no CV content"
                />
              </p>
              <a
                href="/profile"
                className="inline-block rounded-lg bg-[#3B5E45] hover:bg-[#2A4533] text-white text-sm font-medium px-4 py-2"
              >
                <Msg id="jobDetailPage.tailorCv.goToProfile" defaultMessage="Go to your profile" description="Link to profile page" />
              </a>
            </div>
          )}

          {hasAnyContent && activeTab === 'cv' && (
            <>
              {state === 'loading' && (
                <div className="text-center py-8 text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
                  <Msg
                    id="jobDetailPage.tailorCv.loading"
                    defaultMessage="Rewording your existing experience for this job…"
                    description="Loading state while tailoring CV"
                  />
                </div>
              )}

              {state === 'error' && (
                <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                  {errorMessage}
                </div>
              )}

              {state === 'quota-exceeded' && (
                <div className="px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm">
                  <Msg
                    id="jobDetailPage.tailorCv.quotaExceeded"
                    defaultMessage="You've hit today's tailoring limit; it resets tomorrow."
                    description="Shown when the user has used up their daily CV tailoring quota"
                  />
                </div>
              )}

              {state === 'success' && tailoredData && (
                <>
                  <p className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] leading-relaxed">
                    <Msg
                      id="jobDetailPage.tailorCv.disclaimer"
                      defaultMessage="Only your existing experience was reworded and reordered to highlight what this job cares about. Nothing was invented. Review it below before downloading."
                      description="Disclaimer explaining CV tailoring does not fabricate content"
                    />
                  </p>
                  {keywordsHighlighted.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {keywordsHighlighted.map((k) => (
                        <span
                          key={k}
                          className="inline-flex rounded-full bg-[#C8DDCE] dark:bg-[#1A2E22] px-2.5 py-0.5 text-xs text-[#3B5E45] dark:text-[#98CFA8] border border-[#3B5E45]/30 dark:border-[#4A3520]"
                        >
                          {k}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="rounded-xl overflow-hidden border border-[#DDD5C8] dark:border-[#4A3520]">
                    <EuropassCVContent
                      userName={tailoredData.userName}
                      userPhoto={tailoredData.userPhoto}
                      profileTitle={tailoredData.profileTitle}
                      mostRecentWorkExp={tailoredData.mostRecentWorkExp}
                      workExperiencesByDate={tailoredData.workExperiencesByDate}
                      educationByDate={tailoredData.educationByDate}
                      skills={tailoredData.skills}
                      projectsByYear={tailoredData.projectsByYear}
                      formatDateRange={formatDateRange}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {hasAnyContent && activeTab === 'letter' && (
            <>
              {letterState === 'idle' && (
                <div className="text-center py-8 space-y-3">
                  <p className="text-sm text-[#3D3529] dark:text-[#B8A48C]">
                    <Msg
                      id="jobDetailPage.coverLetter.intro"
                      defaultMessage="Draft a cover letter using your profile and this job; you can edit it before downloading."
                      description="Intro copy on the cover letter tab before generation"
                    />
                  </p>
                  <button
                    type="button"
                    onClick={handleGenerateLetter}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#3B5E45] hover:bg-[#2A4533] text-white text-sm font-medium px-4 py-2 cursor-pointer"
                  >
                    <Sparkle className="w-4 h-4" />
                    <Msg
                      id="jobDetailPage.coverLetter.generateCta"
                      defaultMessage="Draft my cover letter"
                      description="Button to opt in to generating a cover letter"
                    />
                  </button>
                </div>
              )}

              {letterState === 'loading' && (
                <div className="text-center py-8 text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
                  <Msg
                    id="jobDetailPage.coverLetter.loading"
                    defaultMessage="Drafting your cover letter…"
                    description="Loading state while drafting the cover letter"
                  />
                </div>
              )}

              {letterState === 'error' && (
                <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                  {letterErrorMessage}
                </div>
              )}

              {letterState === 'quota-exceeded' && (
                <div className="px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 text-sm">
                  <Msg
                    id="jobDetailPage.coverLetter.quotaExceeded"
                    defaultMessage="You've hit today's cover letter limit; it resets tomorrow."
                    description="Shown when the user has used up their daily cover letter quota"
                  />
                </div>
              )}

              {letterState === 'success' && (
                <>
                  <p className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] leading-relaxed">
                    <Msg
                      id="jobDetailPage.coverLetter.editHint"
                      defaultMessage="Edit the text below before copying or downloading; it's yours to adjust."
                      description="Hint that the generated cover letter text is editable"
                    />
                  </p>

                  {(ungroundedCount > 0 || overclaims.length > 0) && (
                    <div className="space-y-1.5">
                      {ungroundedCount > 0 && (
                        <div className="flex items-start gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-300">
                          <WarningCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <Msg
                            id="jobDetailPage.coverLetter.ungroundedFlag"
                            defaultMessage="{count, plural, one {One part} other {# parts}} of this letter wasn't tied to anything specific in your profile; please check {count, plural, one {it's} other {they're}} accurate."
                            description="Warning shown when one or more cover letter paragraphs have no verified profile source"
                            values={{ count: ungroundedCount }}
                          />
                        </div>
                      )}
                      {overclaims.map((skill) => (
                        <div
                          key={skill}
                          className="flex items-start gap-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 text-xs text-amber-800 dark:text-amber-300"
                        >
                          <WarningCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <Msg
                            id="jobDetailPage.coverLetter.overclaimWarning"
                            defaultMessage={`We couldn't find "{skill}" in your profile; make sure this is true.`}
                            description="Warning shown when the letter mentions a job skill not found in the user's profile"
                            values={{ skill }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {groundedParagraphs.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {groundedParagraphs.map((p, i) => (
                        <span
                          key={i}
                          className="inline-flex rounded-full bg-[#C8DDCE] dark:bg-[#1A2E22] px-2.5 py-0.5 text-xs text-[#3B5E45] dark:text-[#98CFA8] border border-[#3B5E45]/30 dark:border-[#4A3520]"
                        >
                          <Msg
                            id="jobDetailPage.coverLetter.basedOn"
                            defaultMessage="Based on: {entries}"
                            description="Provenance chip prefix listing which profile entries a cover letter paragraph draws on"
                            values={{ entries: p.sourceIds.map(resolveSourceLabel).join(', ') }}
                          />
                        </span>
                      ))}
                    </div>
                  )}

                  <textarea
                    value={letterText}
                    onChange={handleLetterTextChange}
                    rows={14}
                    className="w-full rounded-xl border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] text-[#1E1A14] dark:text-[#F2EADF] text-sm p-3 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-[#3B5E45]/40"
                  />
                </>
              )}
            </>
          )}
        </div>

        {activeTab === 'cv' && state === 'success' && tailoredData && (
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#DDD5C8] dark:border-[#4A3520] shrink-0">
            <a
              href={applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-[#342415] dark:bg-[#1F1509] text-[#F2EADF] dark:text-[#C4A882] text-sm font-medium hover:bg-[#4A3520] dark:hover:bg-[#342415]"
            >
              <Msg id="jobDetailPage.applyNow" defaultMessage="Apply" description="Apply now button on job detail page. Kept short (not 'Apply on Company Website') since naming the outbound destination up front reads as alarming; the description text above already sets that expectation" />
            </a>
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#3B5E45] dark:bg-[#2A4533] text-white text-sm font-medium hover:bg-[#2A4533] dark:hover:bg-[#1A2E22] cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <Msg id="jobDetailPage.tailorCv.download" defaultMessage="Download tailored CV" description="Download tailored CV button" />
            </button>
          </div>
        )}

        {activeTab === 'letter' && letterState === 'success' && (
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#DDD5C8] dark:border-[#4A3520] shrink-0">
            <a
              href={applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-[#342415] dark:bg-[#1F1509] text-[#F2EADF] dark:text-[#C4A882] text-sm font-medium hover:bg-[#4A3520] dark:hover:bg-[#342415]"
            >
              <Msg id="jobDetailPage.applyNow" defaultMessage="Apply" description="Apply now button on job detail page. Kept short (not 'Apply on Company Website') since naming the outbound destination up front reads as alarming; the description text above already sets that expectation" />
            </a>
            <button
              type="button"
              onClick={handleCopyLetter}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#DDD5C8] dark:border-[#4A3520] text-[#3D3529] dark:text-[#F2EADF] text-sm font-medium hover:bg-[#F2EADF] dark:hover:bg-[#2A1D0F] cursor-pointer"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? (
                <Msg id="jobDetailPage.coverLetter.copied" defaultMessage="Copied!" description="Confirmation shown briefly after copying the cover letter text" />
              ) : (
                <Msg id="jobDetailPage.coverLetter.copy" defaultMessage="Copy text" description="Button to copy the cover letter text to the clipboard" />
              )}
            </button>
            <button
              type="button"
              onClick={handleDownloadLetter}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#3B5E45] dark:bg-[#2A4533] text-white text-sm font-medium hover:bg-[#2A4533] dark:hover:bg-[#1A2E22] cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <Msg id="jobDetailPage.coverLetter.download" defaultMessage="Download cover letter" description="Button to download the cover letter PDF" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
